/* eslint-disable react-hooks/exhaustive-deps,
react/forbid-component-props,
promise/prefer-await-to-callbacks,
promise/prefer-await-to-then */

import dataCollector from 'braintree-web/data-collector';
import paypalCheckout from 'braintree-web/paypal-checkout';
import classNames from 'classnames';
import { Field, Formik } from 'formik';
import capitalize from 'lodash/capitalize';
import upperCase from 'lodash/upperCase';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import reactCookies from 'react-cookies';
import { DISPLAY_NAME } from '../../../brands/default/config';
import {
  COOKIES,
  PAYMENT_DOD_TRACKING_GATEWAY,
  PAYMENT_METHODS,
  PAYMENT_TYPES,
} from '../../../brands/default/constants';
import { CHECKOUT_PAYMENT_COMPONENT } from '../../../brands/default/content/components/checkout';
import { trackEvent, updateDod } from '../../../helpers/analytics';
import BraintreeService from '../../../helpers/braintreeService';
import {
  allowPayPalOneClick,
  basketHasHighRiskDeal,
} from '../../../helpers/checkout';
import CustomErrorsService from '../../../helpers/customErrorsService';
import { sleep } from '../../../helpers/general';
import PayPalMessaging from '../../PayPalMessaging';
import Message, { MESSAGE_TYPES } from '../../_generic/Message';
import { logToSplunk } from '../../_generic/axiosSplunk/axiosSplunk';
import Spinner from '../../_generic/spinner/Spinner';
import useToast from '../../_generic/toast/UseToast';
import BasketSocialCues from '../BasketSocialCues';
import PaymentAgreeTerms from './PaymentAgreeTerms';
import PaymentSummary from './PaymentSummary';
import VipMessageWrapper from './VipMessageWrapper';
import VipToggleButtons from './VipToggleButtons';
import VoucherContractDisclamer from './VoucherContractDisclamer';

const PaymentPaypal = ({
  afterError,
  atolProtected,
  basketProducts,
  checkoutData,
  currencySymbol,
  customError,
  finalOrderAmount,
  isExclusiveProductInBasket,
  isVipUser,
  isWalletApplied,
  orderComplete,
  orderSummary,
  payFinalOrderAmount,
  providerError,
  setVipOptIn,
  totalQuantity,
  vipOptIn = null,
  vipOptInFinalOrderAmount,
  vipPreButtonMessage,
  welcomeVipEnabled,
  vipPaywall,
}) => {
  const formikRef = React.useRef(null);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [dataCollectorInstance, setDataCollectorInstance] = useState(null);
  const container = '#paypal-button-container';
  const overlayDisabled = 'disabled-overlay';
  const paypalDisableOneClickCheckout =
    reactCookies.load(COOKIES.paypalBilling) === 'b';
  let hasRendered = false;

  const handleVipToggleClick = (vipOptInValue) => {
    setVipOptIn(vipOptInValue);
  };

  const toggleLoaderOn = () => {
    setIsLoading(true);
  };

  const toggleLoaderOff = () => {
    setIsLoading(false);
  };

  const notifyError = (message) => {
    toast.addToast(message, 'toast-error', 'top-right');
  };

  const handleOrderComplete = (order) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }
    toggleLoaderOff();
    // call order complete in payment component
    orderComplete(order);
  };

  const handleOrderError = (error) => {
    if (dataCollectorInstance && dataCollectorInstance.teardown) {
      dataCollectorInstance.teardown();
    }

    if (
      error.data &&
      error.data.errors &&
      Object.keys(error.data.errors).length > 0
    ) {
      // custom errors
      const customErrors = CustomErrorsService.parseErrors(
        error.data.errors,
        error.config.data || {},
      );
      customError({ errors: customErrors });
    } else {
      // default error message
      notifyError(CHECKOUT_PAYMENT_COMPONENT.paymentPaypal.errorDefaultPayment);
    }

    toggleLoaderOff();
    // will reload the checkout
    afterError();
  };

  const tokenizeTransaction = async (paypalInstance, data) => {
    try {
      const tokenizationPayload = await paypalInstance.tokenizePayment(data);
      logToSplunk(
        {
          basketProducts,
          checkoutData,
          component: 'PaymentPaypal',
          isWalletApplied,
          orderAmount: payFinalOrderAmount,
          orderSummary,
          payment: 'Paypal Payment',
          type: 'Paypal Payment data before sending to processor',
          vipOptInFinalOrderAmount,
        },
        'frontend logs',
      );

      return BraintreeService.preparePayment({
        basketProducts,
        credit: false,
        dataCollectorInstance,
        isWalletApplied,
        orderSummary,
        paymentMethod: PAYMENT_METHODS.paypal,
        tokenizationPayload,
      });
    } catch (error) {
      return console.error(error);
    }
  };

  const orderFunction = ({
    fundingSource,
    paypal,
    paypalInstance,
    paymentOptions,
  }) => {
    return function () {
      providerError({});
      toggleLoaderOn();
      const event =
        fundingSource === paypal.FUNDING.PAYPAL
          ? 'paypal_click'
          : 'paypal_credit_click';
      trackEvent(event);
      if (welcomeVipEnabled && vipOptIn) trackEvent('vip_paypalbw_click');
      if (welcomeVipEnabled && !vipOptIn) trackEvent('vip_paypalbwo_click');
      updateDod({ gateway: PAYMENT_DOD_TRACKING_GATEWAY.braintree });
      logToSplunk(
        {
          basketProducts,
          component: 'PaymentPaypal',
          isWalletApplied,
          orderAmount: payFinalOrderAmount,
          orderSummary,
          payment: 'Paypal Payment',
          type: 'paypal details before create payment',
          vipOptInFinalOrderAmount,
        },
        'frontend logs',
      );

      return paypalInstance.createPayment(paymentOptions);
    };
  };

  const setButtonOrderOptions = (fundingSource, paypal, paypalInstance) => {
    return {
      fundingSource,

      onApprove(data) {
        return tokenizeTransaction(paypalInstance, data);
      },

      onCancel() {
        const abortEvent =
          fundingSource === paypal.FUNDING.PAYPAL
            ? 'paypal_aborted'
            : 'paypal_credit_aborted';
        trackEvent(abortEvent);
        toggleLoaderOff();
      },

      onError(error) {
        console.error(error);
        const abortEvent =
          fundingSource === paypal.FUNDING.PAYPAL
            ? 'paypal_aborted'
            : 'paypal_credit_aborted';
        trackEvent(abortEvent);
        toggleLoaderOff();
      },

      onInit(data, actions) {
        document
          .querySelector('#confirmPayment')
          .addEventListener('change', (event) => {
            if (event.target.checked) {
              document
                .querySelector(container)
                .classList.remove(overlayDisabled);
              actions.enable();
            } else {
              document.querySelector(container).classList.add(overlayDisabled);
              actions.disable();
            }
          });
      },

      style: {
        color: fundingSource === paypal.FUNDING.PAYLATER ? 'blue' : 'gold',
      },
    };
  };

  const showDisabledButton = () => {
    document.querySelector(container).classList.add(overlayDisabled);
  };

  const hideDisabledButton = () => {
    document.querySelector(container).classList.remove(overlayDisabled);
  };

  const setPaymentOptions = (payPalPaymentMethodVaulted) => {
    return {
      amount: payFinalOrderAmount.toFixed(2).toString(),
      /*       billingAgreementDetails: {
        description: 'Billing Agreement Message goes here',
      }, */
      currency: upperCase(orderSummary?.currency),
      displayName: capitalize(DISPLAY_NAME),
      enableShippingAddress: false,
      flow: 'checkout',
      intent: 'capture',
      requestBillingAgreement:
        !payPalPaymentMethodVaulted ||
        basketHasHighRiskDeal(basketProducts, BraintreeService.isPostCheckout),
    };
  };

  const setupButton = ({
    fundingSource,
    paypal,
    paypalInstance,
    payPalPaymentMethodVaulted = false,
  }) => {
    const paymentOptions = setPaymentOptions(payPalPaymentMethodVaulted);

    const buttonOrderOptions = setButtonOrderOptions(
      fundingSource,
      paypal,
      paypalInstance,
    );

    buttonOrderOptions.createOrder = orderFunction({
      fundingSource,
      paymentOptions,
      paypal,
      paypalInstance,
    });

    return paypal.Buttons(buttonOrderOptions);
  };

  const setupButtons = ({
    fundingSources,
    paypal,
    paypalInstance,
    payPalPaymentMethodVaulted,
  }) => {
    fundingSources.forEach((fundingSource) => {
      if (fundingSource === paypal.FUNDING.PAYLATER && payFinalOrderAmount < 30)
        return;
      const button = setupButton({
        fundingSource,
        paypal,
        paypalInstance,
        payPalPaymentMethodVaulted,
      });
      if (button.isEligible()) {
        if (button && button.close && hasRendered) {
          button.close();
          hasRendered = false;
        }
        hasRendered = button
          .render(container)
          .then(() => {
            return true;
          })
          .catch((error) => {
            const selector = document.querySelector('#container-element');

            if (selector && selector.children.length > 0) {
              throw new Error(error);
            }

            return false;
          });
      }
    });
  };

  const setupCheckout = async (clientInstance) => {
    if (!payFinalOrderAmount) return;
    try {
      const vault = paypalDisableOneClickCheckout
        ? []
        : await BraintreeService.getVaultedCards(true);
      const payPalPaymentMethodVaulted = vault?.length > 0;
      const checkoutInstance = await paypalCheckout.create({
        autoSetDataUserIdToken:
          payPalPaymentMethodVaulted &&
          !basketHasHighRiskDeal(
            basketProducts,
            BraintreeService.isPostCheckout,
          ) &&
          allowPayPalOneClick(basketProducts, payFinalOrderAmount), // this enables recurring customer flow
        client: clientInstance,
      });
      if (!checkoutInstance) return;

      const paypalInstance = await checkoutInstance.loadPayPalSDK({
        components: 'buttons,messages',
        currency: upperCase(orderSummary?.currency),
        dataAttributes: {
          amount: payFinalOrderAmount.toFixed(2).toString(), // amount needed for recurring customer flow
        },
        'enable-funding': 'paylater',
        intent: 'capture',
        locale: `en_GB`,
      });
      const paypal = window.paypal; // loadPayPalSDK adds https://www.paypal.com/sdk/js script to the window object
      if (paypal) {
        await sleep(1_000); // prevents error firing when button is teared down and rerendered
        const fundingSources = [paypal.FUNDING.PAYPAL, paypal.FUNDING.PAYLATER];
        setupButtons({
          fundingSources,
          paypal,
          paypalInstance,
          payPalPaymentMethodVaulted,
        });
        toggleLoaderOff();
      }
    } catch (error) {
      console.error(`Error creating Paypal Instance:${error}`);
      notifyError(CHECKOUT_PAYMENT_COMPONENT.paymentPaypal.errorBraintree);
    }
  };

  const setupPayPalSDK = async (clientInstance) => {
    if (!clientInstance) return;

    try {
      const response = await dataCollector.create({
        client: clientInstance,
        kount: true,
        paypal: true,
      });
      // At this point, you should access the dataCollectorInstance.deviceData value and provide it
      // to your server, e.g. by injecting it into your form as a hidden input.
      setDataCollectorInstance(response);
      if (response) {
        BraintreeService.deviceData = response.deviceData;
      }
    } catch (error) {
      console.error(error);

      return;
    }
    setupCheckout(clientInstance);
  };

  const setupBraintree = async () => {
    // payment component will be disabled if user is not logged in
    BraintreeService.paymentComplete = handleOrderComplete;
    BraintreeService.paymentError = handleOrderError;
    BraintreeService.paymentType = PAYMENT_TYPES.paypal;
    BraintreeService.totalQuantity = totalQuantity;
    BraintreeService.providerError = providerError;
    const clientInstance = await BraintreeService.getClientInstance();
    setupPayPalSDK(clientInstance);
  };

  useEffect(() => {
    toggleLoaderOn();
    setupBraintree();
    formikRef.current.validateForm();
    if (!isVipUser && welcomeVipEnabled) {
      if (vipOptIn === null) {
        showDisabledButton();
      } else {
        hideDisabledButton();
      }
    }
  }, [payFinalOrderAmount, vipOptIn]);

  return (
    <>
      {isLoading && <Spinner isFullScreen />}
      <Formik
        enableReinitialize
        initialValues={{
          cardCvv: '',
          confirmPayment: true,
          isVipOptedIn: vipOptIn,
        }}
        innerRef={formikRef}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
        }}
        validate={(values) => {
          const errors = {};
          if (!values.confirmPayment) {
            errors.confirmPayment = true;
          }
          if (!isVipUser && welcomeVipEnabled && vipOptIn === null) {
            errors.isVipOptedIn = true;
          }

          return errors;
        }}
      >
        {({ handleSubmit, isValid }) => (
          <form className="" name="payment" onSubmit={handleSubmit}>
            <div className="row payment-body">
              <div className="col-12">
                {
                  CHECKOUT_PAYMENT_COMPONENT.paymentPaypal
                    .redirectionPaypalMessage
                }
                <PaymentAgreeTerms disabled={false} />
                {!vipPaywall && (
                  <BasketSocialCues
                    atolProtected={atolProtected}
                    className="with-margin"
                    isLighter
                    isPostCheckout={BraintreeService.isPostCheckout}
                  />
                )}
                <VoucherContractDisclamer />
                <PaymentSummary
                  {...orderSummary}
                  isPostCheckout={BraintreeService.isPostCheckout}
                  totalQuantity={totalQuantity}
                />
              </div>
              {!isVipUser && welcomeVipEnabled && vipPreButtonMessage && (
                <VipMessageWrapper>{vipPreButtonMessage}</VipMessageWrapper>
              )}
              {!isVipUser && welcomeVipEnabled && (
                <>
                  <VipToggleButtons
                    currencySymbol={currencySymbol}
                    finalOrderAmount={finalOrderAmount}
                    isExclusiveProductInBasket={isExclusiveProductInBasket}
                    setPayButton={handleVipToggleClick}
                    vipOptInFinalOrderAmount={vipOptInFinalOrderAmount}
                  />
                  <Field
                    id="isVipOptedIn"
                    name="isVipOptedIn"
                    type="hidden"
                    value={vipOptIn || ''}
                  />
                  <div className="vip-messages">
                    {isExclusiveProductInBasket && (
                      <div className="vip-pay-button-message">
                        <Message
                          message={
                            CHECKOUT_PAYMENT_COMPONENT.paymentPaypal
                              .vipPayExclusiveDeal
                          }
                        />
                      </div>
                    )}
                    {isExclusiveProductInBasket &&
                      vipOptIn === null &&
                      !isValid && (
                        <div className="vip-pay-button-message">
                          <Message
                            message={
                              CHECKOUT_PAYMENT_COMPONENT.paymentPaypal
                                .vipPayExclusiveButton
                            }
                            showIcon={false}
                            type={MESSAGE_TYPES.error}
                          />
                        </div>
                      )}
                    {!isExclusiveProductInBasket &&
                      vipOptIn === null &&
                      !isValid && (
                        <div className="vip-pay-button-message">
                          <Message
                            message={
                              CHECKOUT_PAYMENT_COMPONENT.paymentPaypal
                                .vipPayButtonMessage
                            }
                            showIcon={false}
                            type={MESSAGE_TYPES.error}
                          />
                        </div>
                      )}
                    {vipOptIn !== null && (
                      <div className="vip-pay-button-message">
                        {
                          CHECKOUT_PAYMENT_COMPONENT.paymentPaypal
                            .vipPayButtonMessageSelected
                        }
                      </div>
                    )}
                  </div>
                </>
              )}
              <div className="col-12">
                <div
                  className={classNames({
                    'vip-pay-button-container': !isVipUser && welcomeVipEnabled,
                  })}
                  id="paypal-button-container"
                />
              </div>
            </div>
          </form>
        )}
      </Formik>

      {!isLoading && (
        <div className="paypal-message-container">
          <PayPalMessaging
            deal={{ price: payFinalOrderAmount }}
            isFeatured
            isFeaturedSlot
            isPayPalDeal
          />
        </div>
      )}

      <style jsx>{`
        .vip-pay-button-container {
          margin: auto;
          width: 75%;
        }
        .vip-messages {
          margin: 0 auto;
          padding: 0 10px;
        }
        .vip-pay-button-message {
          margin: 0 auto 10px auto;
          text-align: center;
        }
        .disabled-overlay {
          position: relative;
          margin-bottom: 12px;
        }
        .disabled-overlay::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: grey;
          opacity: 0.3;
          z-index: 1000;
        }
        .paypal-message-container {
          padding-top: 15px;
        }
      `}</style>
    </>
  );
};

PaymentPaypal.propTypes = {
  afterError: PropTypes.func.isRequired,
  atolProtected: PropTypes.bool,
  basketProducts: PropTypes.array.isRequired,
  checkoutData: PropTypes.object,
  currencySymbol: PropTypes.string,
  customError: PropTypes.func.isRequired,
  finalOrderAmount: PropTypes.number,
  isExclusiveProductInBasket: PropTypes.bool,
  isVipUser: PropTypes.bool,
  isWalletApplied: PropTypes.bool,
  orderComplete: PropTypes.func.isRequired,
  orderSummary: PropTypes.object.isRequired,
  payFinalOrderAmount: PropTypes.number,
  providerError: PropTypes.func.isRequired,
  setVipOptIn: PropTypes.func,
  totalQuantity: PropTypes.number,
  travelDisableButton: PropTypes.bool,
  vipOptIn: PropTypes.bool,
  vipOptInFinalOrderAmount: PropTypes.number,
  vipPaywall: PropTypes.bool,
  vipPreButtonMessage: PropTypes.element,
  welcomeVipEnabled: PropTypes.bool,
};

export default PaymentPaypal;
